"use client";

import React from "react";
import { cn } from "utils/utils";
import { Eye, EyeOff } from "lucide-react";
import { TimelineSeriesConfig } from "modules/cards/components/RosenCardPriceChartTypes";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";

interface LegendItemProps {
  series: TimelineSeriesConfig;
  isVisible: boolean;
  onToggle: () => void;
}

function LegendItem({ series, isVisible, onToggle }: LegendItemProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  return (
    <button
      onClick={onToggle}
      className={cn(
        "flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs transition-colors md:gap-2 md:px-2 md:py-1 md:text-sm",
        isVisible
          ? "bg-base-200/30 hover:bg-base-200/50"
          : "hover:bg-base-200/20 bg-transparent opacity-50"
      )}
    >
      <div className="flex items-center space-x-1.5">
        <div className={cn("h-2 w-4 rounded-sm md:h-2.5 md:w-5", series.color)} />
        <span className="text-base-content/80">{series.name}</span>
      </div>

      <div className="text-base-content/50">
        {isVisible ? (
          <Eye size={isMobile ? 12 : 14} />
        ) : (
          <EyeOff size={isMobile ? 12 : 14} />
        )}
      </div>
    </button>
  );
}

interface ResponsiveChartLegendProps {
  series: TimelineSeriesConfig[];
  visibleSeries: Record<string, boolean>;
  onToggleSeries: (key: string) => void;
  showEventMarkers?: boolean;
  onToggleEventMarkers?: () => void;
  hasEventMarkers?: boolean;
}

export function ResponsiveChartLegend({
  series,
  visibleSeries,
  onToggleSeries,
  showEventMarkers = true,
  onToggleEventMarkers,
  hasEventMarkers = false,
}: ResponsiveChartLegendProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  return (
    <div
      className={cn(
        "mt-2 flex flex-wrap items-center gap-1.5 px-2 md:mt-3 md:gap-2 md:px-0",
        isMobile ? "justify-center" : "justify-start"
      )}
    >
      {series.map((s) => (
        <LegendItem
          key={s.key}
          series={s}
          isVisible={visibleSeries[s.key]}
          onToggle={() => onToggleSeries(s.key)}
        />
      ))}

      {hasEventMarkers && onToggleEventMarkers && (
        <button
          onClick={onToggleEventMarkers}
          className={cn(
            "flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs transition-colors md:gap-2 md:px-2 md:py-1 md:text-sm",
            isMobile ? "mt-1 w-full justify-center" : "ml-auto",
            showEventMarkers
              ? "bg-base-200/30 hover:bg-base-200/50"
              : "hover:bg-base-200/20 bg-transparent opacity-50"
          )}
        >
          <div className="flex items-center space-x-1.5">
            <div className="bg-secondary h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
            <span className="text-base-content/80">Set Releases</span>
          </div>

          <div className="text-base-content/50">
            {showEventMarkers ? (
              <Eye size={isMobile ? 12 : 14} />
            ) : (
              <EyeOff size={isMobile ? 12 : 14} />
            )}
          </div>
        </button>
      )}
    </div>
  );
}
